import React, { useEffect, useState } from 'react';
import { Cookie } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';

const KEY = 'nymia.cookie_consent_v1';

/**
 * Cookie consent banner — GDPR/CCPA compliant.
 *
 * Stores the user's choice in localStorage. Choices:
 *   - 'accepted': all categories (essential + analytics + tracking)
 *   - 'rejected': essential only
 *
 * The banner is hidden once a choice has been recorded.
 * Other parts of the app (Meta Pixel loader, etc.) can read the value via
 * `cookieConsent()` to gate non-essential third-party scripts.
 */
export const cookieConsent = () => {
  if (typeof window === 'undefined') return null;
  try { return localStorage.getItem(KEY); } catch (_e) { return null; }
};

export const hasOptedIn = () => cookieConsent() === 'accepted';

const CookieBanner = () => {
  const { t } = useTranslation('common');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      // Nascondi il banner sul prototipo Creator Page (validazione design isolata)
      if (typeof window !== 'undefined' && window.location.pathname === '/creator-prototype') return;
      if (!localStorage.getItem(KEY)) setVisible(true);
    } catch (_e) { /* private mode — keep hidden */ }
  }, []);

  const choose = (value) => {
    try { localStorage.setItem(KEY, value); } catch (_e) { /* ignore */ }
    setVisible(false);
    // Allow listeners to react (e.g. Meta Pixel loader)
    try { window.dispatchEvent(new CustomEvent('nymia:cookie-consent', { detail: value })); } catch (_e) { /* ignore */ }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-3xl rounded-2xl border border-amber-500/30 bg-neutral-950/95 p-4 text-foreground shadow-2xl backdrop-blur md:bottom-20 md:inset-x-auto md:right-6 md:left-auto md:max-w-md"
      role="dialog"
      aria-label={t('cookie_banner.aria_label')}
      data-testid="cookie-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/15">
          <Cookie className="h-4 w-4 gold-text" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold gold-text">{t('cookie_banner.title')}</p>
          <p className="mt-1 text-[12px] leading-snug text-foreground/80">
            {t('cookie_banner.body')}{' '}
            <a href="/privacy" className="underline underline-offset-2 hover:gold-text">
              {t('cookie_banner.learn_more')}
            </a>
            .
          </p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 sm:justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => choose('rejected')}
          className="text-foreground hover:bg-secondary"
          data-testid="cookie-reject-btn"
        >
          {t('cookie_banner.reject')}
        </Button>
        <Button
          size="sm"
          onClick={() => choose('accepted')}
          className="gold-gradient text-black"
          data-testid="cookie-accept-btn"
        >
          {t('cookie_banner.accept')}
        </Button>
      </div>
    </div>
  );
};

export default CookieBanner;
